/* Four companion tools for the Mutation Analysis category:
   1. MutationBreedingPipeline — 4-step workflow hub
   2. IndianCropMutationDB — pre-loaded Indian crop mutation reference table
   3. MutantVarietyComparison — similarity score + radar chart
   4. DUSTestingGuide — PPV&FR Act descriptors for mutant varieties
*/
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CircleCheck, Search, Sprout, Database as DB, GitCompareArrows, ScrollText,
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from 'recharts';
import { INDIAN_CROP_MUTATION_DB, DUS_MUTANT_DESCRIPTORS } from './mutationSampleData';

const AMBER = '#B45309';
const AMBER_ACTIVE = '#D97706';

/* ============================================================
   1. MUTATION BREEDING PIPELINE
============================================================ */
export function MutationBreedingPipeline() {
  const navigate = useNavigate();
  const steps = [
    {
      n: 1,
      key: 'mutation-dose-response',
      title: 'Dose Response',
      desc: 'Upload germination/survival/height data across doses → determine optimal dose and quadratic fit.',
      next: 'mutation-ld50-gr50',
    },
    {
      n: 2,
      key: 'mutation-ld50-gr50',
      title: 'LD50 / GR50',
      desc: 'From dose-response, calculate lethal-dose-50 and growth-reduction-50 via probit regression.',
      next: 'mutation-m2-generation',
    },
    {
      n: 3,
      key: 'mutation-m2-generation',
      title: 'M2 Screening',
      desc: 'Score chlorophyll + morphological + quantitative mutants; identify segregating families.',
      next: 'mutation-m3-progeny',
    },
    {
      n: 4,
      key: 'mutation-m3-progeny',
      title: 'M3 Confirmation',
      desc: 'Chi-square segregation (3:1 / 15:1), confirm true-breeding mutants ready for yield trials.',
      next: null,
    },
  ];

  const launch = (key) => navigate(`/biometrical-analysis?section=mutation&design=${key}`);

  return (
    <div className="space-y-4" data-testid="mutation-pipeline">
      <div className="rounded-xl p-4 border" style={{ background: '#FFFBEB', borderColor: '#FCD34D' }}>
        <div className="flex items-center gap-2 mb-1">
          <Sprout size={16} style={{ color: AMBER }} />
          <span className="font-display" style={{ fontSize: 15, color: '#78350F' }}>
            End-to-end Mutation Breeding workflow
          </span>
        </div>
        <p className="text-sm" style={{ color: '#92400E' }}>
          Run the 4 core analyses in sequence — each hands off insights to the next.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((s, i) => (
          <div key={s.n} className="rounded-xl p-4 bg-white flex flex-col"
            style={{ border: '1px solid #FCD34D', boxShadow: '0 2px 8px rgba(180,83,9,0.06)' }}
            data-testid={`pipeline-step-${s.n}`}
          >
            <div className="flex items-center justify-between">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ background: AMBER_ACTIVE }}>{s.n}</span>
              {i < steps.length - 1 && <ArrowRight size={14} style={{ color: '#CA8A04' }} />}
            </div>
            <div className="font-semibold mt-2" style={{ color: '#78350F' }}>{s.title}</div>
            <p className="text-xs mt-1 flex-1" style={{ color: '#92400E' }}>{s.desc}</p>
            <button
              onClick={() => launch(s.key)}
              className="mt-3 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              style={{ background: AMBER_ACTIVE, color: 'white' }}
            >
              Launch step {s.n}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-xl p-4 bg-white border border-amber-200">
        <div className="flex items-center gap-2 mb-2">
          <CircleCheck size={14} style={{ color: '#059669' }} />
          <span className="text-xs uppercase tracking-wider font-bold" style={{ color: '#064E3B' }}>
            Full master-report
          </span>
        </div>
        <p className="text-xs text-gray-600">
          After running all 4 steps, head to the <b>Breeding Pipeline</b> card (green banner at the top of
          Biometrical Analysis) to compile a single-PDF master report including ANOVA, LD50/GR50, M2 families,
          and M3 segregation tables.
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   2. INDIAN CROP MUTATION DB
============================================================ */
export function IndianCropMutationDB() {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INDIAN_CROP_MUTATION_DB;
    return INDIAN_CROP_MUTATION_DB.filter(r =>
      Object.values(r).some(v => String(v).toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="space-y-3" data-testid="crop-mutation-db">
      <div className="flex items-center gap-2 rounded-xl p-4"
        style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
        <DB size={16} style={{ color: AMBER }} />
        <span className="font-display" style={{ fontSize: 15, color: '#78350F' }}>
          Indian Crop Mutation Reference Database
        </span>
        <span className="ml-auto text-xs text-amber-700">{INDIAN_CROP_MUTATION_DB.length} crops</span>
      </div>
      <div className="flex items-center gap-2 rounded-lg p-2 bg-white border border-amber-200">
        <Search size={14} style={{ color: '#B45309' }} />
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by crop, mutagen, variety, institute…"
          className="w-full outline-none text-sm"
          data-testid="crop-db-search"
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-amber-200 bg-white">
        <table className="w-full text-xs border-collapse">
          <thead><tr className="bg-amber-100 text-amber-900">
            {['Crop', 'Scientific name', 'Mutagen', 'LD50', 'GR50', 'Mutation freq.', 'Released varieties', 'Institute'].map(h => (
              <th key={h} className="px-2 py-1.5 border border-amber-200 text-left text-[11px] font-semibold">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.crop} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
                <td className="px-2 py-1.5 border border-amber-200 font-semibold">{r.crop}</td>
                <td className="px-2 py-1.5 border border-amber-200 italic text-gray-600">{r.latin}</td>
                <td className="px-2 py-1.5 border border-amber-200">{r.mutagen}</td>
                <td className="px-2 py-1.5 border border-amber-200 font-mono">{r.ld50}</td>
                <td className="px-2 py-1.5 border border-amber-200 font-mono">{r.gr50}</td>
                <td className="px-2 py-1.5 border border-amber-200 font-mono text-amber-900">{r.freq}</td>
                <td className="px-2 py-1.5 border border-amber-200">{r.varieties}</td>
                <td className="px-2 py-1.5 border border-amber-200 text-[11px] text-gray-700">{r.institute}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-500">
        Sources: ICAR Handbook on Mutation Breeding (Kharkwal 2012) · FAO/IAEA Mutant Variety Database ·
        BARC NA&amp;BTD annual reports · NBPGR plant-germplasm catalogue. Approximate values — always
        confirm with the mutagen-facility SOP for your specific cultivar.
      </p>
    </div>
  );
}

/* ============================================================
   3. MUTANT VARIETY COMPARISON
============================================================ */
export function MutantVarietyComparison() {
  const [traits] = useState(['Plant height', 'Days to flower', 'Grain yield', '1000-grain weight', 'Disease score']);
  const [mutant, setMutant] = useState({ 'Plant height': 85, 'Days to flower': 95, 'Grain yield': 45, '1000-grain weight': 38, 'Disease score': 3 });
  // ICAR registered variety reference profiles (illustrative)
  const refs = [
    { name: 'HD-3086 (Wheat)', profile: { 'Plant height': 95, 'Days to flower': 102, 'Grain yield': 47, '1000-grain weight': 40, 'Disease score': 2 } },
    { name: 'PBW-725', profile: { 'Plant height': 90, 'Days to flower': 100, 'Grain yield': 46, '1000-grain weight': 39, 'Disease score': 3 } },
    { name: 'Sharbati Sonora (Mutant)', profile: { 'Plant height': 85, 'Days to flower': 95, 'Grain yield': 44, '1000-grain weight': 38, 'Disease score': 3 } },
  ];

  const similarity = (a, b) => {
    let ss = 0, n = 0;
    traits.forEach(t => {
      if (a[t] != null && b[t] != null) {
        const d = Math.abs(a[t] - b[t]) / ((Math.abs(a[t]) + Math.abs(b[t])) / 2 || 1);
        ss += 1 - Math.min(d, 1);
        n++;
      }
    });
    return n ? (ss / n) * 100 : 0;
  };

  const scored = refs.map(r => ({ ...r, similarity_percent: +similarity(mutant, r.profile).toFixed(2) }));
  const best = scored.reduce((a, b) => (a.similarity_percent > b.similarity_percent ? a : b), scored[0]);

  // Radar data
  const radarData = traits.map(t => ({
    trait: t,
    Mutant: mutant[t],
    ...Object.fromEntries(refs.map(r => [r.name, r.profile[t]])),
  }));

  return (
    <div className="space-y-4" data-testid="variety-comparison">
      <div className="rounded-xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
        <div className="flex items-center gap-2">
          <GitCompareArrows size={16} style={{ color: AMBER }} />
          <span className="font-display" style={{ fontSize: 15, color: '#78350F' }}>
            Mutant Variety Comparison Tool
          </span>
        </div>
        <p className="text-sm mt-1" style={{ color: '#92400E' }}>
          Enter your mutant's trait values — compare with ICAR/NBPGR reference varieties. Similarity is 1 − mean relative deviation.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Input form */}
        <div className="rounded-xl p-4 bg-white border border-amber-200">
          <div className="font-semibold text-sm mb-2" style={{ color: '#78350F' }}>Your mutant</div>
          <div className="space-y-2">
            {traits.map(t => (
              <label key={t} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-700 flex-1">{t}</span>
                <input
                  type="number" step="0.1"
                  value={mutant[t]}
                  onChange={e => setMutant(m => ({ ...m, [t]: +e.target.value }))}
                  className="w-24 px-2 py-1 text-sm border border-amber-200 rounded font-mono"
                  data-testid={`mutant-input-${t}`}
                />
              </label>
            ))}
          </div>
        </div>

        {/* Similarity scores */}
        <div className="rounded-xl p-4 bg-white border border-amber-200">
          <div className="font-semibold text-sm mb-2" style={{ color: '#78350F' }}>Similarity to reference varieties</div>
          <div className="space-y-2">
            {scored.map(s => (
              <div key={s.name} className={`rounded-lg p-2 ${s === best ? 'bg-amber-50 border border-amber-400' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{s.name}</span>
                  <span className="text-sm font-mono font-bold" style={{ color: AMBER }}>{s.similarity_percent}%</span>
                </div>
                <div className="h-1.5 mt-1 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${s.similarity_percent}%`,
                    background: s === best ? AMBER_ACTIVE : '#CA8A04',
                  }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] mt-2 text-gray-600">
            Closest match: <b>{best.name}</b> — {best.similarity_percent}% profile overlap.
          </p>
        </div>
      </div>

      {/* Radar chart */}
      <div className="rounded-xl p-4 bg-white border border-amber-200">
        <div className="font-semibold text-sm mb-2" style={{ color: '#78350F' }}>Multi-trait radar</div>
        <div style={{ width: '100%', height: 340 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#FCD34D" />
              <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis tick={{ fontSize: 10 }} />
              <Radar name="Mutant" dataKey="Mutant" stroke={AMBER_ACTIVE} fill={AMBER_ACTIVE} fillOpacity={0.35} />
              {refs.map((r, i) => (
                <Radar key={r.name} name={r.name} dataKey={r.name}
                  stroke={['#2563EB', '#059669', '#7C3AED'][i % 3]}
                  fill={['#2563EB', '#059669', '#7C3AED'][i % 3]}
                  fillOpacity={0.12} />
              ))}
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   4. DUS TESTING GUIDE (MUTANTS)
============================================================ */
export function DUSTestingGuide() {
  return (
    <div className="space-y-4" data-testid="dus-guide">
      <div className="rounded-xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
        <div className="flex items-center gap-2">
          <ScrollText size={16} style={{ color: AMBER }} />
          <span className="font-display" style={{ fontSize: 15, color: '#78350F' }}>
            DUS Testing Guide for Mutant Varieties
          </span>
        </div>
        <p className="text-sm mt-1" style={{ color: '#92400E' }}>
          Under the Protection of Plant Varieties and Farmers' Rights Act, 2001 (PPV&amp;FR Authority, Govt. of India),
          a mutant variety must be distinct, uniform, and stable for at least the descriptors below. Testing is carried
          out over minimum two growing seasons at PPV&amp;FRA-notified test centres.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {DUS_MUTANT_DESCRIPTORS.map(g => (
          <div key={g.group} className="rounded-xl p-4 bg-white border border-amber-200">
            <div className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: '#B45309' }}>
              {g.group}
            </div>
            <ul className="text-sm space-y-1 list-disc pl-4">
              {g.descriptors.map(d => <li key={d}>{d}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="rounded-xl p-4" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: '#78350F' }}>
          Procedural checklist
        </div>
        <ol className="text-sm space-y-1 list-decimal pl-5">
          <li>Register the candidate mutant variety with PPV&amp;FRA (Form A) and pay the prescribed fee.</li>
          <li>Deposit seed with NBPGR for long-term safeguarding (IC number).</li>
          <li>Nominate two most-similar "reference varieties" for comparative DUS test.</li>
          <li>Conduct DUS at ≥2 locations across ≥2 years; submit descriptor data sheet per crop.</li>
          <li>Submit the crop-specific DUS filled test-guideline (Section 29 of the Act).</li>
          <li>Pursue parallel VCU (Value for Cultivation and Use) trials under AICRP for varietal release.</li>
        </ol>
      </div>

      <p className="text-[10px] text-gray-500">
        Source: PPV&amp;FR Authority, Government of India — <i>Specific Guidelines for the Conduct of Tests for DUS</i>.
        For any specific crop, download the crop-wise test guidelines from plantauthority.gov.in.
      </p>
    </div>
  );
}
